import { create } from 'zustand';
import { tokenStorage, type AuthTokens } from '@shared/api';

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

export interface AdminIdentity {
  id: string;
  email: string;
  displayName: string | null;
}

const IDENTITY_KEY = 'aidu.admin.identity';

function readIdentity(): AdminIdentity | null {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY);
    return raw ? (JSON.parse(raw) as AdminIdentity) : null;
  } catch {
    return null;
  }
}

function writeIdentity(identity: AdminIdentity | null): void {
  try {
    if (identity) localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
    else localStorage.removeItem(IDENTITY_KEY);
  } catch {
    /* ignore */
  }
}

interface SessionState {
  status: SessionStatus;
  identity: AdminIdentity | null;
  hydrate: () => void;
  setAuthenticated: (tokens: AuthTokens, identity: AdminIdentity) => void;
  setIdentity: (identity: AdminIdentity) => void;
  clear: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'unknown',
  identity: null,
  hydrate: () => {
    tokenStorage.hydrateFromStorage();
    const access = tokenStorage.getAccess();
    const refresh = tokenStorage.getRefresh();
    const isAuthed = !!(access || refresh);
    set({
      status: isAuthed ? 'authenticated' : 'anonymous',
      identity: isAuthed ? readIdentity() : null,
    });
  },
  setAuthenticated: (tokens, identity) => {
    tokenStorage.set(tokens);
    writeIdentity(identity);
    set({ status: 'authenticated', identity });
  },
  setIdentity: (identity) => {
    writeIdentity(identity);
    set({ identity });
  },
  clear: () => {
    tokenStorage.clear();
    writeIdentity(null);
    set({ status: 'anonymous', identity: null });
  },
}));
